import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import usePageTitle from "../hooks/usePageTitle";
import { FaArrowLeft, FaShieldAlt } from "react-icons/fa";
import Logo from "./Logo";

const SafetyTips = () => {
  const navigate = useNavigate();
  usePageTitle("Safety Tips");
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="relative z-10">
        {/* Header */}
        <motion.div
          className="container mx-auto px-4 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            variants={itemVariants}
            className="flex items-center gap-4 mb-8"
          >
            <motion.button
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-romantic-pink hover:text-white transition-colors duration-300"
              whileHover={{ x: -5 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaArrowLeft className="text-xl" />
              <span className="font-semibold">Back to Home</span>
            </motion.button>
          </motion.div>

          <motion.div variants={itemVariants} className="text-center mb-12">
            <div className="flex justify-center items-center gap-4 flex-col">
              <div className="text-center lg:text-left">
                <img
                  src="/assets/moon-logo.png"
                  alt="Logo"
                  className="w-32 mx-auto lg:mx-0"
                />
              </div>
              <div className="text-center lg:text-left">
                <img
                  src="/assets/png-logo.png"
                  alt="Logo"
                  className="w-40 mx-auto lg:mx-0 pb-2"
                />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              Safety Tips
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Your vibe matters — but your safety matters even more.
            </p>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto mt-2">
              Whether you're matching, chatting, or planning your first date, here are some simple safety rules to keep your experience fun and secure.
            </p>
          </motion.div>
        </motion.div>

        <motion.div 
          variants={containerVariants} 
          className="bg-gray-900/80 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 md:p-10 space-y-10 max-w-5xl mx-auto mb-16"
        >
          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink flex items-center">
              {/* <FaShieldAlt className="mr-2 text-blue-600" /> */}
              🔐 1. Protect Your Personal Info
            </h2>
            <p className="text-gray-300">
              Don't drop sensitive info too early — no phone numbers, home address, workspace, or financial details.
            </p>
            <p className="text-gray-400 italic">Stay mysterious… in a healthy way.</p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🧩 2. Keep Conversations Inside Datenite</h2>
            <p className="text-gray-300">
              Our in-app chat is built with security checks and scam detection.
            </p>
            <p className="text-gray-300">
              Stay here until you're fully comfortable.
            </p>
            <p className="text-red-400 font-medium">
              If someone tries to move you to WhatsApp or Telegram instantly → 🚩
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🤳 3. Video Call Before Meeting</h2>
            <p className="text-gray-300">
              We get it — people can be ✨creative✨ with photos.
            </p>
            <p className="text-gray-300">
              Use Datenite's built-in video call to verify the person is real before you step out.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🕵️‍♂️ 4. Look Out for Suspicious Behavior</h2>
            <p className="text-gray-300">
              Block or report users who:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-300">
              <li>Ask for money</li>
              <li>Act pushy or manipulative</li>
              <li>Send inappropriate content</li>
              <li>Offer "business opportunities"</li>
              <li>Seem too good to be true (they usually are 😭)</li>
            </ul>
            <p className="text-gray-200 font-medium">We take reports seriously.</p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">📍 5. Meet in Public Places</h2>
            <p className="text-gray-300">
              First few dates = café, mall, restaurant, park — not their house, not yours.
            </p>
            <p className="text-gray-300">
              Let friends know where you are.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🚗 6. Arrange Your Own Transport</h2>
            <p className="text-gray-300">
              Don't let anyone pick you up or drop you.
            </p>
            <p className="text-gray-200 font-medium">Stay independent. Stay safe.</p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">💡 7. Trust Your Gut</h2>
            <p className="text-gray-300">
              If something feels off… it is off.
            </p>
            <p className="text-gray-300">
              You can unmatch, block, report — instantly.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🥤 8. Watch Your Drinks</h2>
            <p className="text-gray-300">
              If you're meeting IRL, keep your drink with you.
            </p>
            <p className="text-gray-300">
              No leaving it unattended.
            </p>
            <p className="text-gray-200 font-medium">
              Stay alert, even if the date is cute.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">💳 9. Don't Share Financial Information</h2>
            <p className="text-gray-300">
              No bank details.
            </p>
            <p className="text-gray-300">
              No OTP.
            </p>
            <p className="text-gray-300">
              No "I'll return it tomorrow" stories.
            </p>
            <p className="text-gray-200 font-medium">
              Dating should cost zero stress.
            </p>
          </motion.div>

          <motion.div variants={itemVariants} className="space-y-2">
            <h2 className="text-2xl font-bold text-romantic-pink">🛡️ 10. We're Here For You</h2>
            <p className="text-gray-300">
              Datenite has:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-300">
              <li>Profile verification</li>
              <li>Manual moderation</li>
              <li>24/7 reporting support</li>
            </ul>
            <p className="text-red-400 font-medium">
              If anything feels wrong, report immediately.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default SafetyTips;
